/**
 * Light Theme Configuration
 *
 * Complete MUI theme with glassmorphism, glows, and modern aesthetics.
 */

import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { baseColors, lightPalette } from './palette';
import { focusRings, glassEffects, glowEffects, lightShadows } from './shadows';
import { muiTransitions, transitionPresets } from './transitions';
import { typographyVariants } from './typography';

// ============================================================================
// Theme Options
// ============================================================================

const lightThemeOptions: ThemeOptions = {
  palette: lightPalette,
  typography: typographyVariants,
  transitions: muiTransitions,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    lightShadows[1],
    lightShadows[2],
    lightShadows[2],
    lightShadows[3],
    lightShadows[3],
    lightShadows[3],
    lightShadows[3],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[4],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[5],
    lightShadows[6],
  ],
  components: {
    // ========================================================================
    // Button
    // ========================================================================
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          textTransform: 'none',
          transition: transitionPresets.button,
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.MuiButton-contained.MuiButton-colorPrimary': {
            background: `linear-gradient(135deg, ${baseColors.primary[500]} 0%, ${baseColors.primary[600]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColors.primary[400]} 0%, ${baseColors.primary[500]} 100%)`,
              boxShadow: `${lightShadows[3]}, ${glowEffects.primary.medium}`,
            },
          },
          '&.MuiButton-contained.MuiButton-colorSecondary': {
            background: `linear-gradient(135deg, ${baseColors.secondary[500]} 0%, ${baseColors.secondary[600]} 100%)`,
            '&:hover': {
              boxShadow: `${lightShadows[3]}, ${glowEffects.secondary.medium}`,
            },
          },
        },
        contained: {
          boxShadow: lightShadows[2],
          '&:hover': {
            boxShadow: `${lightShadows[3]}, ${glowEffects.primary.subtle}`,
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            backgroundColor: 'rgba(99, 91, 255, 0.04)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(99, 91, 255, 0.04)',
          },
        },
      },
    },

    // ========================================================================
    // Card
    // ========================================================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${baseColors.neutral[200]}`,
          boxShadow: lightShadows[2],
          transition: transitionPresets.card,
          overflow: 'visible',
          '&:hover': {
            boxShadow: `${lightShadows[3]}, ${glowEffects.mixed.subtle}`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiCardActionArea-focusHighlight': {
            backgroundColor: baseColors.primary[500],
            opacity: 0,
            transition: transitionPresets.allFast,
          },
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0.04,
          },
          '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
            opacity: 0.08,
          },
        },
      },
    },

    // ========================================================================
    // Paper (for glassmorphism)
    // ========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: lightShadows[1],
        },
        elevation2: {
          boxShadow: lightShadows[2],
        },
        elevation3: {
          boxShadow: lightShadows[3],
        },
        elevation4: {
          boxShadow: lightShadows[4],
        },
      },
    },

    // ========================================================================
    // TextField / Input
    // ========================================================================
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: transitionPresets.input,
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: baseColors.primary[300],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            boxShadow: focusRings.primary,
          },
        },
        notchedOutline: {
          borderColor: baseColors.neutral[300],
          transition: transitionPresets.input,
        },
      },
    },

    // ========================================================================
    // Chip
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          '&:hover': {
            boxShadow: glowEffects.primary.subtle,
          },
        },
      },
    },

    // ========================================================================
    // Dialog / Modal
    // ========================================================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: `${lightShadows[5]}, ${glowEffects.mixed.subtle}`,
          border: `1px solid ${baseColors.neutral[200]}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: 12,
        },
      },
    },

    // ========================================================================
    // Menu / Dropdown
    // ========================================================================
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: lightShadows[4],
          border: `1px solid ${baseColors.neutral[200]}`,
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '12px 16px',
          minHeight: 44,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: baseColors.neutral[100],
          },
          '&.Mui-selected': {
            backgroundColor: `${baseColors.primary[50]}`,
            '&:hover': {
              backgroundColor: `${baseColors.primary[100]}`,
            },
          },
        },
      },
    },

    // ========================================================================
    // Tooltip
    // ========================================================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: baseColors.neutral[800],
          color: baseColors.neutral[0],
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.75rem',
          fontWeight: 500,
          boxShadow: lightShadows[3],
        },
        arrow: {
          color: baseColors.neutral[800],
        },
      },
    },

    // ========================================================================
    // Avatar
    // ========================================================================
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: `2px solid ${baseColors.neutral[0]}`,
          boxShadow: lightShadows[2],
        },
      },
    },

    // ========================================================================
    // Drawer
    // ========================================================================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          borderRadius: 0,
          boxShadow: lightShadows[4],
        },
      },
    },

    // ========================================================================
    // AppBar
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: lightShadows[1],
          backdropFilter: glassEffects.light.backdropBlur,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        },
      },
    },

    // ========================================================================
    // FormControlLabel — add breathing room between control and label (switch, checkbox, radio)
    // ========================================================================
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: 12,
        },
      },
    },

    // ========================================================================
    // Switch
    // ========================================================================
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: baseColors.primary[500],
              opacity: 1,
            },
          },
        },
        track: {
          borderRadius: 22,
          backgroundColor: baseColors.neutral[300],
        },
        thumb: {
          boxShadow: lightShadows[2],
        },
      },
    },

    // ========================================================================
    // Skeleton (Shimmer)
    // ========================================================================
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: baseColors.neutral[200],
        },
      },
    },

    // ========================================================================
    // Tabs
    // ========================================================================
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          transition: transitionPresets.allFast,
        },
      },
    },

    // ========================================================================
    // List
    // ========================================================================
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: baseColors.neutral[100],
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: baseColors.neutral[100],
          },
          '&.Mui-selected': {
            backgroundColor: baseColors.primary[50],
            '&:hover': {
              backgroundColor: baseColors.primary[100],
            },
          },
        },
      },
    },

    // ========================================================================
    // Divider
    // ========================================================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: baseColors.neutral[200],
        },
      },
    },
  },
};

// ============================================================================
// Create and Export Theme
// ============================================================================

export const lightTheme = createTheme(lightThemeOptions);

export default lightTheme;
